/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { Modal } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import Text from 'src/components/ts-components/Text'
import SendEmailIcon from 'src/components/icons/SendEmailIcon'
import Button from 'src/components/ts-components/Button'
import { TextInput } from 'src/components/ts-components/TextInput'
import { useCallback, useMemo, useState } from 'react'

const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

type Props = {
  isOpen: boolean
  onClose: () => void
  onSend: (email: string) => Promise<void> | void
  type: 'team' | 'player'
}

const SendTestEmailModal = ({ isOpen, onClose, onSend, type }: Props) => {
  const [email, setEmail] = useState('')
  const [isSending, setIsSending] = useState(false)
  const isValid = useMemo(() => emailRegex.test(email), [email])

  const handleClose = useCallback(() => {
    setEmail('')
    setIsSending(false)
    onClose()
  }, [onClose])

  const handleSend = useCallback(async () => {
    if (!isValid || isSending) {
      return
    }
    try {
      setIsSending(true)
      await onSend(email)
      setEmail('')
    } finally {
      setIsSending(false)
    }
  }, [email, isSending, isValid, onSend])

  return (
    <Modal
      open={isOpen}
      onCancel={handleClose}
      footer={null}
      centered
      maskClosable={false}
      zIndex={10020}
      styles={{
        content: {
          backgroundColor: colors.SECONDARY_300,
          padding: 0,
          borderRadius: 16,
          border: `1px solid ${colors.SECONDARY_100}`,
          overflow: 'hidden',
        },
        mask: {
          backdropFilter: 'blur(2px)',
          backgroundColor: 'rgba(6, 10, 16, 0.8)',
          zIndex: 10019,
        },
        wrapper: {
          zIndex: 10020,
        },
      }}
      css={css`
        .ant-modal-header {
          display: none;
        }
        .ant-modal-body {
          display: flex;
          flex-direction: column;
          padding: 24px;
          gap: 16px;
          background: ${colors.SECONDARY_300};
        }
        .ant-modal-content {
          border-radius: 16px;
          overflow: hidden;
        }
      `}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
        `}
      >
        <SendEmailIcon size={48} color={colors.PRIMARY} />
        <Text variant='heading1' weight='semiBold' color={colors.WHITE}>
          Send Test Email
        </Text>
        <div
          css={css`
            align-self: stretch;
            text-align: center;
          `}
        >
          <Text variant='paragraphMedium' weight='regular' color={colors.WHITE}>
            Enter an email address to receive a test {type} registration email.
          </Text>
        </div>
        <div
          css={css`
            align-self: stretch;
            display: flex;
            flex-direction: column;
            gap: 8px;
          `}
        >
          <TextInput
            name='testEmail'
            value={email}
            setValue={setEmail}
            label='Email Address'
            placeholder='john.doe@example.com'
            autoComplete='on'
            autoFocus
            fieldErrors={[]}
            customError={{}}
          />
        </div>
        <div
          css={css`
            align-self: stretch;
            display: flex;
            justify-content: flex-end;
            gap: 16px;
            margin-top: 8px;
          `}
        >
          <Button text='Cancel' onClick={handleClose} variant='secondary' />
          <Button
            text={isSending ? 'SENDING…' : 'SEND'}
            onClick={handleSend}
            disabled={!isValid || isSending}
            isLoading={isSending}
            variant='primary'
          />
        </div>
      </div>
    </Modal>
  )
}

export default SendTestEmailModal
